import React, { forwardRef, ReactElement, useEffect, useLayoutEffect } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { ISubHeaderProps } from '../SubHeader/SubHeader';
import { IPageProps } from '../Page/Page';
import { useSelector } from 'react-redux';
import { RootState } from 'stores/store';
import { authMenu, dashboardMenu, profileMenu } from 'menu';
import { AccountStatusEnum, ErrorResponseEnum } from 'enums';

interface IPageWrapperProps {
	isProtected?: boolean;
	title?: string;
	description?: string;
	children:
		| ReactElement<ISubHeaderProps>[]
		| ReactElement<IPageProps>
		| ReactElement<IPageProps>[];
	className?: string;
}
const PageWrapper = forwardRef<HTMLDivElement, IPageWrapperProps>(
	({ isProtected = true, title, description, className, children }, ref) => {
		useLayoutEffect(() => {
			// @ts-ignore
			document.getElementsByTagName('TITLE')[0].text = `${title ? `${title} | ` : ''}${
				process.env.REACT_APP_SITE_NAME
			}`;
			// @ts-ignore
			document
				?.querySelector('meta[name="description"]')
				.setAttribute('content', description || process.env.REACT_APP_META_DESC || '');
		});

		const loggedIn = useSelector((state: RootState) => state.auth.isLogged);
		const user = useSelector((state: RootState) => state.auth.user);

		const navigate = useNavigate();

		useEffect(() => {
			const unprotectedPaths = [
				authMenu.login.path,
				authMenu.confirmLogin.path,
				authMenu.forgetGroup.subMenu.confirmation.path,
				authMenu.forgetGroup.subMenu.forgetPassword.path,
				authMenu.forgetGroup.subMenu.resetPassword.path,
			];
			const pendingCompletionRoute = [dashboardMenu.dashboard.path, profileMenu.profile.path];
			const currentPath = window.location.pathname;
			if (isProtected && !loggedIn && !unprotectedPaths.includes(currentPath)) {
				navigate(`../${authMenu.login.path}`);
			}
			if (
				isProtected &&
				loggedIn &&
				(!user?.email_verified_at || !user?.passport_photo || !user?.account_name) &&
				!pendingCompletionRoute.includes(currentPath)
			) {
				navigate(`../${profileMenu.profile.path}`, {
					state: { error: ErrorResponseEnum.PROFILE_INCOMPLETE },
				});
			}

			if (
				isProtected &&
				loggedIn &&
				!user?.branch_id &&
				!pendingCompletionRoute.includes(currentPath)
			) {
				navigate(`../${profileMenu.profile.path}`, {
					state: { error: ErrorResponseEnum.BRANCH_NOT_SET },
				});
			}

			if (
				isProtected &&
				loggedIn &&
				user?.status === AccountStatusEnum.REJECTED &&
				!pendingCompletionRoute.includes(currentPath)
			) {
				navigate(`../${profileMenu.profile.path}`, {
					state: { error: ErrorResponseEnum.REJECTED },
				});
			}

			if (
				isProtected &&
				loggedIn &&
				user?.status === AccountStatusEnum.PENDING &&
				!pendingCompletionRoute.includes(currentPath)
			) {
				navigate(`../${profileMenu.profile.path}`, {
					state: { error: ErrorResponseEnum.PENDING_APPROVAL },
				});
			}

			return () => {};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		return (
			<div ref={ref} className={classNames('page-wrapper', 'container-fluid', className)}>
				{children}
			</div>
		);
	},
);
PageWrapper.displayName = 'PageWrapper';

export default PageWrapper;
